import { ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { isFirstTimeUser } from '@/utils/welcomeUtils';

interface WelcomePageGuardProps {
  children: ReactNode;
  redirectTo?: string;
}

/**
 * Component that protects the welcome page from being accessed by returning users
 * Only first-time users should be able to access the welcome page
 * 
 * Usage:
 * <WelcomePageGuard>
 *   <WelcomePage />
 * </WelcomePageGuard>
 */
export default function WelcomePageGuard({ 
  children, 
  redirectTo = '/' 
}: WelcomePageGuardProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [isAllowed, setIsAllowed] = useState(true);
  const location = useLocation();
  
  useEffect(() => {
    // Check if user is a first-time visitor
    const firstTimeUser = isFirstTimeUser();
    console.log('WelcomePageGuard check:', { 
      path: location.pathname,
      firstTimeUser: firstTimeUser,
      allowAccess: firstTimeUser
    });
    
    // Only allow first-time users to access the welcome page
    setIsAllowed(firstTimeUser);
    setIsLoading(false);
  }, [location.pathname]);
  
  // While checking, show nothing
  if (isLoading) {
    console.log('WelcomePageGuard still loading...');
    return null;
  }
  
  // If the user has already seen the welcome page, redirect away
  if (!isAllowed) {
    console.log('User has already seen welcome, redirecting to:', redirectTo);
    return <Navigate to={redirectTo} replace />;
  }
  
  // Allow first-time users to see the welcome page
  console.log('Showing welcome page to first-time user');
  return <>{children}</>;
} 